import React, { useEffect, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useAppSelector, useIsMultiPortfolioTenant } from '../../common/hooks';
import { getGoalPriorityProbability } from '../../common/helper';
import { Icon } from '../../components/atoms/Icon/Icon';
import { Typography, BasicButton } from '../../components/atoms';
import { Box, DialogProps, styled, useTheme } from '@mui/material';
import { GoalPriorityChip, TableWithCustomRender } from '../../components/molecules';
import { getLastUpdateDateFromGoals } from '../../utils';

import {
    EWS_TERM_TAB_KEY_MAP,
    FLOW2_CATEGORY_GOALS,
    TERM_LOCALIZATION,
    iwsGoalTypeMapper,
    STATUSVARIANTS,
    TermTabType
} from '../../common/constants';
import { selectUnsavedChangeDate } from '../../features/unsaved-plan-changes';
// import { GoalCategoryType } from '../../common/types';

import { ReactComponent as CheckmarkFilledIcon } from '../../assets/icons/checkmarkFilled.svg';

// import { getGoalCategory } from '../../features/client-goals';
import dayjs from 'dayjs';
import UpdateFundingGoalsStepModal, {
    AmountInput,
    InfoBanner,
    TABLE_ROW_HEIGHT,
    TableFrame,
    amountCellSx,
    labelTextSx,
    stepButtonSx,
    tableBodyCellSx,
    tableHeaderCellSx
} from './UpdateFundingGoalsStepModal';

const GoalTypeIconSmall = styled(Box)<{ color?: string; backgroundColor?: string }>(
    ({ theme, color, backgroundColor }) => ({
        display: 'flex',
        width: '20px',
        height: '20px',
        padding: '2px',
        borderRadius: '50%',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: backgroundColor || theme.palette.neutral.white,
        flexShrink: 0,
        '& svg': {
            width: '12px',
            height: '12px',
            color
        }
    })
);

// const LoaderOverlay = styled(Box)(() => ({
//     display: 'flex',
//     alignItems: 'center',
//     justifyContent: 'center',
//     position: 'absolute',
//     top: 0,
//     left: 0,
//     right: 0,
//     bottom: 0,
//     backgroundColor: 'rgba(255, 255, 255, 0.7)',
//     borderRadius: '10px',
//     zIndex: 1
// }));

type UpdateGoalsProps = {
    formFieldDataSource: any[];
    currentRunpipeData: any;
    isOpen: boolean;
    onClose: () => void;
    // eslint-disable-next-line no-unused-vars
    onSubmit: (isEdited: boolean, values: any) => void;
    handleBackToFunding: () => void;
    selectedTermIndex: TermTabType;
    transitionDuration?: DialogProps['transitionDuration'];
};

type ColumnAlign = 'left' | 'center' | 'right' | 'inherit' | 'justify';

const UpdateGoals: React.FC<UpdateGoalsProps> = ({
    formFieldDataSource,
    currentRunpipeData,
    isOpen,
    onClose,
    handleBackToFunding,
    onSubmit,
    selectedTermIndex,
    transitionDuration
}) => {
    const { t } = useTranslation();
    const isMultiPortfolioTenant = useIsMultiPortfolioTenant();
    const theme = useTheme();
    const formFieldSourcesRef = useRef<any[]>([]);

    const { goeConfig } = useAppSelector((state) => state.global);
    const adviceGoals = useAppSelector((state) => state.adviceGoals);
    const goalPriorityValue = goeConfig?.['goalPriorityProbabilities'];
    const globalData = adviceGoals?.global as any;
    const isCompletePlan = globalData?.status === STATUSVARIANTS.COMPLETE;
    const unsavedChangeDate = useAppSelector((state) =>
        selectUnsavedChangeDate(state, 'goals', EWS_TERM_TAB_KEY_MAP[selectedTermIndex])
    );
    const planLastUpdateDate = globalData?.lastUpdateDate ? dayjs(globalData.lastUpdateDate).format('DD MMM YYYY') : '';
    const goalsLastUpdateDate = getLastUpdateDateFromGoals(formFieldDataSource || []);
    // AEX-3724: a goal change in this term that isn't saved with the plan yet shows its own date. Otherwise
    // complete plans show the plan's last update date, incomplete ones the latest goal update;
    // if that date is missing, fall back to the other instead of leaving the date blank.
    const lastUpdateDate = unsavedChangeDate
        ? dayjs(unsavedChangeDate).format('DD MMM YYYY')
        : isCompletePlan
          ? planLastUpdateDate || goalsLastUpdateDate
          : goalsLastUpdateDate || planLastUpdateDate;
    // const language = useAppSelector((state) => state?.global?.globalConfig?.language);
    // const [goalCategoryLoading, setGoalCategoryLoading] = useState<boolean>(false);

    useEffect(() => {
        formFieldSourcesRef.current = formFieldDataSource.map((field) => {
            const thresholdValue = Math.round(
                getGoalPriorityProbability(goalPriorityValue, field?.data?.['goalPriority']) * 100
            );
            const goal_runpipeData = currentRunpipeData?.find(
                (run: any) => run.goalId === field?.data?.goalId
            )?.response;
            return {
                ...field?.data,
                // isGoalRealistic: currentRunpipeData?.find((run: any) => run.goalName === field?.data?.goalId)?.response
                //     ?.body?.analysisReport?.isGoalRealistic,
                isGoalRealistic:
                    Math.round(Number(goal_runpipeData?.body?.analysisReport?.currentGoalProbability) * 100) >=
                    thresholdValue
            };
        });
    }, [formFieldDataSource, currentRunpipeData]);

    const checkIsSourceEdited = (original: any[], current: any[]): boolean => {
        let tempOriginal = JSON.parse(JSON.stringify(original));
        let tempCurrent = JSON.parse(JSON.stringify(current));
        const isSameValue = (left: any, right: any): boolean => {
            if (left === right) return true;
            if (Array.isArray(left) && Array.isArray(right)) {
                if (left.length !== right.length) return false;
                return left.every((item, i) => isSameValue(item, right[i]));
            }
            if (left && right && typeof left === 'object' && typeof right === 'object') {
                if ('isGoalRealistic' in left) delete left.isGoalRealistic;
                if ('isGoalRealistic' in right) delete right.isGoalRealistic;

                const leftKeys = Object.keys(left);
                const rightKeys = Object.keys(right);
                if (leftKeys.length !== rightKeys.length) return false;
                return leftKeys.every(
                    (key) => Object.prototype.hasOwnProperty.call(right, key) && isSameValue(left[key], right[key])
                );
            }
            return false;
        };
        if (tempOriginal.length !== tempCurrent.length) return true;
        return tempOriginal.some((item: any, i: string | number) => !isSameValue(item, tempCurrent[i]));
    };

    // const fetchGoalCategory = async (
    //     targetDate: string | Dayjs,
    //     startDate: string | Dayjs | null = null,
    //     isRetirementGoal: boolean = false
    // ) => {
    //     let goalObj: GoalCategoryType = {
    //         goalName: 'currentGoal',
    //         goalTargetDate: dayjs(targetDate).format('DD-MM-YYYY')
    //     };
    //     if (!isRetirementGoal && targetDate) {
    //         if (startDate) {
    //             goalObj = {
    //                 ...goalObj,
    //                 goalStartDate: dayjs(startDate).format('DD-MM-YYYY')
    //             };
    //         }
    //         const data = {
    //             language: language ? language[0] : 'en',
    //             goals: [goalObj]
    //         };
    //         try {
    //             setGoalCategoryLoading(true);
    //             const response = await getGoalCategory(data);
    //             if (response && response.goalCategoryResult && response.goalCategoryResult.currentGoal) {
    //                 return response.goalCategoryResult;
    //             }
    //         } catch (error) {
    //             console.error('Error fetching goal category:', error);
    //         } finally {
    //             setGoalCategoryLoading(false);
    //         }
    //     }
    // };

    const formFieldHeaders: {
        label: string;
        key: string;
        align: ColumnAlign;
        width: string;
        headerSx?: any;
        cellSx?: any;
        // eslint-disable-next-line no-unused-vars
        render?: (value: any, row: any) => React.ReactNode;
    }[] = [
        {
            label: t('NAME'),
            key: 'goalName',
            align: 'left',
            // Figma column widths: every column but Name is fixed, Name takes the rest.
            width: 'auto',
            headerSx: tableHeaderCellSx,
            cellSx: { ...tableBodyCellSx, pr: 2 },
            // eslint-disable-next-line no-unused-vars
            render: (value: any, row: any) => {
                const gSpecs = FLOW2_CATEGORY_GOALS.find((goal) => row['type'] === goal.type);
                return (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            width: '100%',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '8px'
                        }}
                    >
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <Typography
                                label={row['goalName']}
                                variant="body.small"
                                sx={{
                                    ...labelTextSx,
                                    color: theme.palette.semantic.text.default
                                }}
                            />
                            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '4px' }}>
                                <GoalTypeIconSmall color={gSpecs?.color} backgroundColor={gSpecs?.backgroundColor}>
                                    <Icon id={row['type']} size={12} />
                                </GoalTypeIconSmall>
                                <Typography
                                    label={row['wealthGoalType']}
                                    variant="body.small"
                                    sx={{
                                        ...labelTextSx,
                                        color: theme.palette.semantic.text.muted
                                    }}
                                />
                            </Box>
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                flexShrink: 0
                            }}
                        >
                            {row['isGoalRealistic'] ? (
                                <CheckmarkFilledIcon width={20} height={20} />
                            ) : (
                                <span style={{ display: 'inline-flex' }}>
                                    <Icon id="warning-triangle" size={20} />
                                </span>
                            )}
                        </Box>
                    </Box>
                );
            }
        },
        {
            label: t('PRIORITY'),
            key: 'goalPriority',
            align: 'left',
            width: theme.spacing(18),
            headerSx: tableHeaderCellSx,
            cellSx: tableBodyCellSx,
            render: (value: string) => <GoalPriorityChip goalPriority={value} />
        },
        {
            label: t('DATE_TEXT'),
            key: 'targetDate',
            align: 'left',
            width: theme.spacing(20),
            headerSx: tableHeaderCellSx,
            cellSx: {
                ...tableBodyCellSx,
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: '19px',
                whiteSpace: 'nowrap'
            },
            render: (value: any, row: any) => {
                let startVal = row['incomeStartDate'];
                if (row['type'] === iwsGoalTypeMapper.retirement) {
                    startVal = row['planStartRetirement'];
                }
                const startFmt = startVal ? dayjs(startVal, 'DD-MM-YYYY').format('MMM YYYY') : null;
                const endFmt = value ? dayjs(value, 'DD-MM-YYYY').format('MMM YYYY') : null;
                if (startFmt && endFmt) return `${startFmt} - ${endFmt}`;
                return endFmt || startFmt || '-';
            }
        },
        {
            label: `${t('TEXT_GOAL_AMOUNT')}`,
            key: 'goalAmount',
            align: 'right',
            width: theme.spacing(30),
            headerSx: tableHeaderCellSx,
            cellSx: amountCellSx,
            render: (value: any, row: any) => {
                const isRetirement = row['type'] === iwsGoalTypeMapper.retirement;
                const displayValue = isRetirement ? row['targetedRetirementIncome'] : value;
                return (
                    <AmountInput
                        onChange={(value: number | string) => {
                            if (isRetirement) {
                                row.targetedRetirementIncome = value;
                            } else {
                                row.goalAmount = value;
                            }
                        }}
                        value={displayValue}
                    />
                );
            }
        },
        {
            label: t('FREQUENCY'),
            key: 'frequency',
            align: 'left',
            width: theme.spacing(12),
            headerSx: tableHeaderCellSx,
            cellSx: {
                ...tableBodyCellSx,
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: '19px'
            },
            render: (value: any) => {
                if (!value) return t('GOALS_INLINE_ONE_TIME');
                return value.toLowerCase() === 'annually' ? t('ANNUAL') : t(value.toUpperCase());
            }
        }
    ];

    return (
        <UpdateFundingGoalsStepModal
            open={isOpen}
            onClose={onClose}
            transitionDuration={transitionDuration}
            title={
                    isMultiPortfolioTenant
                    ? t('UPDATE_GOALS_TITLE', { selected_term: t(TERM_LOCALIZATION[selectedTermIndex]) })
                    : t('UPDATE_GOALS_TITLE_DEFAULT')
            }
            subTitle={t('UPDATE_FUNDING_POPUP_SUBTITLE', { last_update_date: lastUpdateDate })}
            actions={
                <>
                    <BasicButton
                        onClick={() => {
                            // await dispatch(updateCurrentStep(2));
                            // onClose();
                            handleBackToFunding();
                        }}
                        variant="outlined"
                        sx={stepButtonSx}
                    >
                        {t('BACK_TO_PRV_STEP')}
                    </BasicButton>
                    <BasicButton
                        onClick={() => {
                            onSubmit(
                                checkIsSourceEdited(
                                    formFieldDataSource.map((f) => f?.data),
                                    formFieldSourcesRef.current
                                ),
                                formFieldSourcesRef.current
                            );
                            onClose();
                        }}
                        variant="contained"
                        disableElevation
                        sx={stepButtonSx}
                    >
                        {t('CLIENT_ONBOARDING_CONTD_BTN')}
                    </BasicButton>
                </>
            }
        >
            <InfoBanner>
                <Trans i18nKey={'UPDATE_GOALS_INFO_TEXT'} components={{ strong: <strong /> }} />
            </InfoBanner>
            {/* Account Card */}
            <TableFrame>
                <TableWithCustomRender
                    headers={formFieldHeaders}
                    dataSource={formFieldSourcesRef.current}
                    variant="ftds"
                    embedded="all"
                    rowHeight={TABLE_ROW_HEIGHT}
                    conditionalRowFormat={(row: any) => {
                        return !row.isGoalRealistic
                            ? {
                                  '&.MuiTableRow-root': {
                                      boxShadow: `inset 4px 0 0 0 ${theme.palette.semantic.bd.warning}`
                                  }
                              }
                            : undefined;
                    }}
                />
            </TableFrame>
        </UpdateFundingGoalsStepModal>
    );
};

// const PrioritySelector: React.FC<{
//     value: string;
//     // eslint-disable-next-line no-unused-vars
//     onChange?: (value: string | string[]) => void;
// }> = ({ value, onChange }) => {
//     const theme = useTheme();
//     const { t } = useTranslation();

//     return (
//         <ComboSelect
//             onSelectChange={(val) => {
//                 onChange && onChange(val);
//             }}
//             selectedValue={value}
//             name={`wealth-goal-priority`}
//             placeholder={t('PRIORITY_TEXT_SELECT') + `(${priorityOptionsFlow2(t).length})`}
//             options={priorityOptionsFlow2(t)}
//             listItemStyle={{
//                 padding: '0px 14px'
//             }}
//             renderMenuItem={(option, index = 0) => (
//                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
//                     <Chip
//                         label={`${index + 1} - ${option.label}`}
//                         size="small"
//                         sx={{
//                             backgroundColor: getPriorityColor(theme, option.value),
//                             fontWeight: 600,
//                             minWidth: '100px',
//                             borderRadius: '16px'
//                         }}
//                     />
//                 </Box>
//             )}
//             renderValue={(selected) => {
//                 const selectedOption = priorityOptionsFlow2(t).find((option) => option.value === selected);
//                 if (selectedOption) {
//                     return (
//                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
//                             <Chip
//                                 label={`${selectedOption.index + 1} - ${selectedOption.label}`}
//                                 size="small"
//                                 sx={{
//                                     backgroundColor: getPriorityColor(theme, selectedOption.value),
//                                     fontWeight: 600,
//                                     minWidth: '100px',
//                                     borderRadius: '16px'
//                                 }}
//                             />
//                         </Box>
//                     );
//                 }
//                 return null;
//             }}
//         />
//     );
// };

export default UpdateGoals;
